/**
 * Hook to get enabled field configurations for OrderManagement
 * Combines static field configurations with configuration context
 */

import {useFieldConfigurationContext} from '@hconnect/tuc-processes/configuration/useConfigurationContext'
import {useMix} from '@hconnect/tuc-processes/fields/mix/useMix'
import {usePlusLoad} from '@hconnect/tuc-processes/orderManagement/orderChange/plusLoad/usePlusLoad'
import {useMemo} from 'react'

import {
  DateFieldButton,
  IntervalsFieldButton,
  IntervalsStep,
  MaterialFieldButton,
  MaterialStep,
  MaterialTestingFieldButton,
  MaterialTestingField,
  MaterialVariationsStep,
  NotesFieldButton,
  NotesField,
  PoNumberFieldButton,
  PoNumberField,
  QuantityFieldButton,
  SiteContactFieldButton,
  SiteContactField,
  TimeFieldButtonWithBanner
} from '../../shared/order-editor/components/fields'
import {OrderManagementDateStep} from '../components/fields/Date/OrderManagementDateStep'
import {OrderManagementQuantityStep} from '../components/fields/Quantity/OrderManagementQuantityStep'
import {OrderManagementTimeStep} from '../components/fields/Time/OrderManagementTimeStep'
import type {FieldConfiguration} from '../types/fieldConfiguration'
import {useEnhancedLoadSpacingScenario} from '../../orderIntake/hooks/useEnhancedLoadSpacingScenario'

/**
 * Get field configurations filtered by configuration context
 * Returns only enabled fields based on configuration.field.isEnabled
 */
export const useOrderManagementFieldConfigurations = (): FieldConfiguration[] => {
  const loadSpacingConfiguration = useFieldConfigurationContext('loadSpacing')
  const poNumberConfiguration = useFieldConfigurationContext('poNumber')
  const additionalNotesConfiguration = useFieldConfigurationContext('additionalNotes')
  const testingConfiguration = useFieldConfigurationContext('testing')

  const loadSpacingScenario = useEnhancedLoadSpacingScenario()
  const {availableMaterialVariations} = useMix()
  const {isEnabled: isPlusLoadEnabled} = usePlusLoad()

  return useMemo(() => {
    const configs: FieldConfiguration[] = [
      {
        fieldId: 'material',
        label: 'orderIntake.rmc.orderManagement.editSteps.material',
        FieldButtonComponent: MaterialFieldButton,
        EditorComponent: MaterialStep,
        isEnabled: true,
        subSteps: [
          {
            fieldId: 'materialVariations',
            label: 'orderIntake.rmc.orderManagement.editSteps.materialVariations',
            EditorComponent: MaterialVariationsStep,
            isEnabled: availableMaterialVariations.isEnabled
          }
        ]
      },
      {
        fieldId: 'quantity',
        label: isPlusLoadEnabled
          ? 'orderIntake.rmc.orderManagement.quantityChange.plusLoad.title'
          : 'orderIntake.rmc.orderManagement.editSteps.quantity',
        FieldButtonComponent: QuantityFieldButton,
        EditorComponent: OrderManagementQuantityStep,
        isEnabled: true
      },
      {
        fieldId: 'intervals',
        label: 'orderIntake.rmc.orderManagement.editSteps.intervals',
        FieldButtonComponent: IntervalsFieldButton,
        EditorComponent: IntervalsStep,
        isEnabled:
          loadSpacingConfiguration.isEnabled &&
          (loadSpacingScenario.flowRate ||
            loadSpacingScenario.loadSpace ||
            loadSpacingScenario.timeOnJobSite)
      },
      {
        fieldId: 'date',
        label: 'orderIntake.rmc.orderManagement.editSteps.date',
        FieldButtonComponent: DateFieldButton,
        EditorComponent: OrderManagementDateStep,
        isEnabled: true
      },
      {
        fieldId: 'time',
        label: 'orderIntake.rmc.orderManagement.editSteps.time',
        FieldButtonComponent: TimeFieldButtonWithBanner,
        EditorComponent: OrderManagementTimeStep,
        isEnabled: true
      },
      {
        fieldId: 'materialTesting',
        label: 'orderIntake.rmc.orderManagement.editSteps.materialTesting',
        FieldButtonComponent: MaterialTestingFieldButton,
        EditorComponent: MaterialTestingField,
        isEnabled: testingConfiguration.isEnabled
      },
      {
        fieldId: 'siteContact',
        label: 'orderIntake.rmc.orderManagement.editSteps.siteContact',
        FieldButtonComponent: SiteContactFieldButton,
        EditorComponent: SiteContactField,
        isEnabled: true
      },
      {
        fieldId: 'poNumber',
        label: 'orderIntake.rmc.orderManagement.editSteps.poNumber',
        FieldButtonComponent: PoNumberFieldButton,
        EditorComponent: PoNumberField,
        isEnabled: poNumberConfiguration.enabled
      },
      {
        fieldId: 'additionalNotes',
        label: 'orderIntake.rmc.orderManagement.editSteps.additionalNotes',
        FieldButtonComponent: NotesFieldButton,
        EditorComponent: NotesField,
        isEnabled: additionalNotesConfiguration.enabled
      }
    ]

    return configs.filter((config) => config.isEnabled)
  }, [
    isPlusLoadEnabled,
    poNumberConfiguration.enabled,
    additionalNotesConfiguration.enabled,
    testingConfiguration.isEnabled,
    loadSpacingConfiguration.isEnabled,
    loadSpacingScenario,
    availableMaterialVariations.isEnabled
  ])
}
